import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LayoutDashboard, TrendingUp, FileText, Building2, User, LogOut, Moon, Sun, Award } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/sales', icon: TrendingUp, label: 'Performance Commerciale' },
    { path: '/subscription', icon: FileText, label: 'Souscription et Tarification' },
    { path: '/agencies', icon: Building2, label: 'Agences' },
  ];

  return (
    <div className="fixed left-0 top-0 h-full w-64 border-r bg-card/50 backdrop-blur-xl hidden lg:block" data-testid="sidebar">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 border-b border-border/60">
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', color: 'hsl(var(--primary))' }}>
            <img src="/logo.png" alt="InsurBoot" className="w-8 h-8 object-contain" />
            InsurBoot
          </h1>
          <p className="text-xs text-muted-foreground mt-1">Intelligence Décisionnelle</p>
          <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 mt-3 flex items-center gap-1 w-fit" data-testid="ia-act-sidebar-badge">
            <Award size={12} />
            <span className="text-xs">Certifié IA Act</span>
          </Badge>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <div
                  className={`sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg ${
                    isActive ? 'active' : ''
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium text-sm">{item.label}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border/60 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className="flex-1"
              data-testid="theme-toggle-button"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="flex-1"
              data-testid="logout-button"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};